/**
 * Home layout Y, "The sheet" (v2, after the CTO + investor reviews). Spreadsheet-first: the headline
 * sits over a working sheet of a real film, with the store badges as the main ask and upload as a
 * quiet link. Then the app in your pocket (a real recording + Isaac's spot), the comparison, the AI
 * section (your AI picks, you stay in charge), the real uploader, pricing, and the closing line the
 * investor liked. Cream paper, dark product screens, one cherry accent.
 */
import React, { useState } from "react";
import { HOME3_CSS } from "./home3Css";
import { AiSection, ClipSheet, PhoneVideo, PlanCards, StoreBadges, UploadCard, scrollToId, useHomeFonts } from "./kit";
import { GetAppBar, H3Footer, H3Nav, useHomeActions } from "./chrome";
import { BADGES, NAV, PLANS, REAL_EDITOR_URL, SHEET, Y } from "../../constants/homeLayoutsText";
import UploadBlock from "../../simple/UploadBlock";
import { UPLOAD_HERE_LABEL } from "../../constants/simpleHomeText";

/** The 3D hub film, opened from a button in the hero. */
const TourModal: React.FC<{ onClose: () => void }> = ({ onClose }) => (
  <div className="y-modal" role="dialog" aria-modal="true" aria-label={Y.tour} onClick={(e) => e.target === e.currentTarget && onClose()}>
    <video src="/home/hub-tour.mp4" poster="/home/hub-tour.jpg" controls autoPlay playsInline />
    <button type="button" className="x" aria-label="Close" onClick={onClose}>
      ×
    </button>
  </div>
);

const LayoutYPage: React.FC = () => {
  useHomeFonts();
  const act = useHomeActions();
  const [tour, setTour] = useState(false);
  return (
    <div className="h3 h3-y">
      <style>{HOME3_CSS + Y_CSS}</style>
      <H3Nav
        onLight
        links={[
          { id: "y-hero", label: NAV.how },
          { id: "y-phone", label: NAV.phone },
          { id: "y-ai", label: NAV.ai },
          { id: "y-pricing", label: NAV.pricing },
        ]}
      />

      <section className="y-hero" id="y-hero">
        <div className="wrap">
          <span className="eyebrow">{Y.eyebrow}</span>
          <h1>
            {Y.title1}
            <br />
            {Y.title2}
            <span className="y-hl">{Y.titleHl}</span>
          </h1>
          <div className="y-row">
            <p className="lede">{Y.sub}</p>
            <div className="y-ctas">
              <span className="y-ask">{Y.getApp}</span>
              <StoreBadges size="md" tag="y-hero" />
              <div className="y-links">
                <button type="button" onClick={() => scrollToId("h3-upload")}>
                  {Y.upload} →
                </button>
                <button type="button" onClick={() => setTour(true)}>
                  ▶ {Y.tour}
                </button>
              </div>
            </div>
          </div>
          <UploadBlock skin="cream" label={UPLOAD_HERE_LABEL} />
          <div className="y-sheet">
            <ClipSheet />
          </div>
          <div className="h3-under">
            <span>
              {SHEET.real} {SHEET.try}
            </span>
            <a className="h3-real" href={REAL_EDITOR_URL} target="_blank" rel="noreferrer">
              {SHEET.openReal}
            </a>
          </div>
        </div>
      </section>

      <section id="y-phone" className="y-phone">
        <div className="wrap y-phone-grid">
          <div>
            <span className="eyebrow">{Y.phoneEyebrow}</span>
            <h2 className="h2">{Y.phoneTitle}</h2>
            <p className="lede" style={{ marginTop: 18 }}>
              {Y.phoneBody}
            </p>
            <div id="h3-get" style={{ marginTop: 28 }}>
              <StoreBadges qr onDark tag="y-phone" />
            </div>
          </div>
          <div className="y-phones">
            <PhoneVideo src="/home/stop-cutting.mp4" poster="/home/stop-cutting.jpg" label="Stop cutting your videos: a 24-second CherryClipz spot" />
          </div>
        </div>
      </section>

      <section id="y-diff">
        <div className="wrap">
          <div className="two">
            <div>
              <span className="eyebrow">{Y.diffEyebrow}</span>
              <h2 className="h2">{Y.diffTitle}</h2>
            </div>
            <p className="lede">{Y.diffBody}</p>
          </div>
          <div className="y-cmp" role="table" aria-label="How CherryClipz compares">
            <div className="row" role="row">
              {Y.diffHead.map((h, k) => (
                <span key={k} role="columnheader" className={k === 3 ? "us" : ""}>
                  {h}
                </span>
              ))}
            </div>
            {Y.diffRows.map((r) => (
              <div className="row" role="row" key={r[0]}>
                {r.map((c, k) => (
                  <span key={k} role={k ? "cell" : "rowheader"} className={k === 0 ? "k" : k === 3 ? "v us" : "v"}>
                    {c}
                  </span>
                ))}
              </div>
            ))}
          </div>
        </div>
      </section>

      <AiSection id="y-ai" />

      <section className="y-start">
        <div className="wrap">
          <div className="y-start-box">
            <div>
              <h2>{Y.startTitle}</h2>
              <p>{Y.startBody}</p>
            </div>
            <UploadCard />
          </div>
        </div>
      </section>

      <section id="y-pricing" style={{ paddingTop: 0 }}>
        <div className="wrap">
          <div className="price-head">
            <div>
              <span className="eyebrow">{PLANS.eyebrow}</span>
              <h2 className="h2">{PLANS.title}</h2>
            </div>
            <button type="button" className="link" onClick={act.pricing}>
              {PLANS.compare}
            </button>
          </div>
          <PlanCards plans={PLANS.items} perMonth={PLANS.perMonth} onPrimary={() => scrollToId("h3-upload")} onPlans={act.pricing} />
        </div>
      </section>

      <section className="y-close">
        <div className="wrap">
          <h2>
            {Y.closeTitle1}
            <br />
            <span className="rw">{Y.closeTitle2}</span>
          </h2>
          <StoreBadges size="md" tag="y-close" />
        </div>
      </section>

      <H3Footer onLight />
      <GetAppBar text={BADGES.free} tag="y" />
      {tour && <TourModal onClose={() => setTour(false)} />}
    </div>
  );
};

const Y_CSS = `
.h3-y .y-hero{padding-block:clamp(40px,6vw,76px) clamp(56px,7vw,90px)}
.h3-y h1{font-weight:800;font-size:clamp(52px,9.4vw,124px);line-height:.9;letter-spacing:-.05em;margin-top:22px}
.h3-y .y-hl{position:relative;color:#fff;padding-inline:.06em;isolation:isolate;white-space:nowrap}
.h3-y .y-hl::before{content:"";position:absolute;inset:.1em -.04em .02em;background:var(--cherry);border-radius:.1em;transform:skewX(-6deg);transform-origin:left;z-index:-1;animation:ypen .7s .35s cubic-bezier(.6,0,.2,1) both}
@keyframes ypen{from{transform:skewX(-6deg) scaleX(0)}}
.h3-y .y-row{display:grid;grid-template-columns:minmax(0,1fr) minmax(0,1fr);gap:clamp(24px,5vw,72px);align-items:end;margin-top:clamp(24px,3vw,36px)}
.h3-y .y-ctas{display:grid;gap:12px;justify-items:start}
.h3-y .y-ask{font:700 17px/1.2 var(--fd);letter-spacing:-.01em}
.h3-y .y-links{display:flex;flex-wrap:wrap;gap:6px 22px}
.h3-y .y-links button{border:0;background:transparent;padding:0;font:500 15px/1.4 var(--fb);color:var(--ink2);text-decoration:underline;text-decoration-color:var(--line2);text-underline-offset:4px}
.h3-y .y-links button:hover{color:var(--ink);text-decoration-color:var(--cherry)}
.h3-y .y-sheet{margin-top:clamp(36px,4vw,52px)}
@media (max-width:860px){.h3-y .y-row{grid-template-columns:1fr}}

.h3-y .y-phone{background:#120d0f;color:#f4ede4;--ink:#f4ede4;--ink2:#b9aea8;--ink3:#877c77;--line:rgba(244,237,228,.1);--line2:rgba(244,237,228,.18);--card:#1b1416;--cherry-t:#ff4d78}
.h3-y .y-phone-grid{display:grid;grid-template-columns:minmax(0,1fr) minmax(0,1fr);gap:clamp(30px,5vw,80px);align-items:center}
.h3-y .y-phones{display:flex;justify-content:center;align-items:center;gap:clamp(12px,2.4vw,30px)}
.h3-y .y-phones .side{width:min(22vw,270px);transform:translateY(24px)}
.h3-y .y-phones .h3-phonewrap .h3-device{width:min(25vw,300px)}
@media (max-width:900px){.h3-y .y-phone-grid{grid-template-columns:1fr}.h3-y .y-phones .side{display:none}.h3-y .y-phones .h3-phonewrap .h3-device{width:min(76vw,300px)}}

.h3-y .y-cmp{margin-top:clamp(36px,4vw,56px);border:1px solid var(--line2);border-radius:18px;overflow:hidden;background:var(--card)}
.h3-y .y-cmp .row{display:grid;grid-template-columns:minmax(150px,.8fr) repeat(3,minmax(0,1fr))}
.h3-y .y-cmp .row>*{padding:18px 22px;border-top:1px solid var(--line);min-width:0}
.h3-y .y-cmp .row>*+*{border-left:1px solid var(--line)}
.h3-y .y-cmp .row:first-child>*{border-top:0;font:500 12px/1.2 var(--fm);letter-spacing:.1em;text-transform:uppercase;color:var(--ink3);padding-block:14px}
.h3-y .y-cmp .k{font:500 12.5px/1.4 var(--fm);color:var(--ink3)}
.h3-y .y-cmp .v{font:500 17px/1.35 var(--fd);letter-spacing:-.01em;color:var(--ink2)}
.h3-y .y-cmp .us{background:var(--soft);color:var(--ink);font-weight:600}
.h3-y .y-cmp .row:first-child .us{color:var(--cherry-t)}
@media (max-width:820px){
  .h3-y .y-cmp .row{grid-template-columns:1fr 1fr 1fr}
  .h3-y .y-cmp .k{grid-column:1/-1;border-left:0!important;padding-bottom:0;border-top:1px solid var(--line2)}
  .h3-y .y-cmp .k + *{border-left:0}
  .h3-y .y-cmp .row:first-child>:first-child{display:none}
  .h3-y .y-cmp .row:first-child>:nth-child(2){border-left:0}
  .h3-y .y-cmp .row>*{padding:12px 14px}
  .h3-y .y-cmp .v{font-size:15px;border-top:0}
}

.h3-y .y-start-box{border-radius:28px;background:#15100f;color:#f4ede4;padding:clamp(28px,5vw,64px);display:grid;grid-template-columns:minmax(0,1fr) minmax(0,1.1fr);gap:clamp(24px,4vw,56px);align-items:center}
.h3-y .y-start-box h2{font-weight:800;font-size:clamp(40px,5.6vw,76px);line-height:.95;letter-spacing:-.045em}
.h3-y .y-start-box p{color:#b3a7a1;margin-top:16px;max-width:40ch}
@media (max-width:860px){.h3-y .y-start-box{grid-template-columns:1fr}}

.h3-y .y-close{text-align:center;border-top:1px solid var(--line)}
.h3-y .y-close .wrap{display:grid;justify-items:center;gap:26px}
.h3-y .y-close h2{font-weight:800;font-size:clamp(42px,6.6vw,92px);line-height:.95;letter-spacing:-.05em}
.h3-y .y-close .h3-stores{justify-content:center}

.h3-y .y-modal{position:fixed;inset:0;z-index:13000;background:rgba(8,5,6,.85);display:grid;place-items:center;padding:24px 16px}
.h3-y .y-modal video{max-height:min(86vh,900px);max-width:100%;aspect-ratio:9/16;border-radius:22px;background:#000}
.h3-y .y-modal .x{position:absolute;top:14px;right:14px;width:44px;height:44px;border-radius:50%;border:0;background:rgba(255,255,255,.14);color:#fff;font-size:22px}
`;

export default LayoutYPage;
